import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';

import type { ButtonVariant } from './Button';
import styles from './Button.module.css';

/**
 * A link that looks exactly like a `Button`.
 *
 * For the commands that must stay real links — a CSV export served as a
 * download, where navigating to the URL is what hands the response to the
 * browser's own download machinery (and middle-click / copy-link keep
 * working). Drawing them as underlined text made the same command look
 * different on one screen from every other; this shares Button's stylesheet,
 * so the two cannot drift apart.
 */
export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  variant?: Exclude<ButtonVariant, 'icon'>;
  /** Leading glyph, 16px, decorative. */
  icon: ReactNode;
  children: ReactNode;
}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant = 'secondary', icon, children, className, ...rest },
  ref,
) {
  return (
    <a
      {...rest}
      ref={ref}
      className={[styles['btn'], styles[variant], styles['link'], className].filter(Boolean).join(' ')}
    >
      <span className={styles['iconSlot']} aria-hidden="true">
        {icon}
      </span>
      {children}
    </a>
  );
});
